/**
 * Focus plumbing shared by every overlay. Written against the DOM rather than a
 * library because the rules are short and a dependency here would be the only
 * runtime dependency in the package.
 */

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');

const isVisible = (element: HTMLElement): boolean =>
  // jsdom has no layout, so offsetParent is always null there. `hidden`,
  // `display:none` via inline style and `aria-hidden` are the checks that work
  // in both environments; a real browser additionally hides nothing else that
  // matters inside a modal.
  !element.hasAttribute('hidden') &&
  element.getAttribute('aria-hidden') !== 'true' &&
  element.style.display !== 'none' &&
  element.style.visibility !== 'hidden';

/** Focusable descendants in tab order. Negative tabindex is excluded. */
export const getFocusable = (container: HTMLElement): HTMLElement[] =>
  [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => element.tabIndex >= 0 && isVisible(element),
  );

/**
 * Keep Tab inside `container`. Returns true when the event was handled, so the
 * caller can decide whether to also preventDefault for its own reasons.
 */
export const trapTab = (container: HTMLElement, event: KeyboardEvent): boolean => {
  if (event.key !== 'Tab') return false;

  const focusable = getFocusable(container);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (first === undefined || last === undefined) {
    // Nothing to move to: hold focus on the container itself.
    event.preventDefault();
    container.focus();
    return true;
  }

  const active = document.activeElement;

  if (event.shiftKey && (active === first || active === container)) {
    event.preventDefault();
    last.focus();
    return true;
  }

  if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
    return true;
  }

  if (active === null || !container.contains(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return true;
  }

  return false;
};

/** Move focus into an overlay: the requested node, else the first focusable. */
export const focusInitial = (container: HTMLElement, preferred?: HTMLElement | null): void => {
  if (preferred !== undefined && preferred !== null) {
    preferred.focus();
    return;
  }
  const [first] = getFocusable(container);
  (first ?? container).focus();
};
